export enum CUSTOM_SCHEME {
  /**
   * * 외부 앱 실행
   * ? 웰로 앱의 외부에서 앱을 실행 (외부 앱 또는 외부 웹 브라우저)
   */
  EXTERNAL_PREFIX = 'external-app::',
  /**
   * * 내부 앱 실행
   * ? 웰로 앱 내부에서 앱을 실행 (웹뷰가 갈아끼워지는)
   */
  IN_APP_PREFIX = 'in-app::',
  /**
   * * 내부 앱 외부 앱 실행
   */
  IN_APP_EXTERNAL_PREFIX = 'in-app-external-app::',
  /**
   * * IOS 웰로 앱 실행
   * ? 웹 링크 일 경우 인앱 브라우저가 열리고, app 서브도메인이 있을 경우 앱 내 웹뷰로 열림
   * ! 외부에서 웰로 앱으로 돌아오는 용도로만 사용
   */
  WELLO_APP_PREFIX = 'welfarehello://action?web_link=',
  /**
   * * 안드로이드 웰로 앱 실행
   * ? 웹 링크 일 경우 인앱 브라우저가 열리고, app 서브도메인이 있을 경우 앱 내 웹뷰로 열림
   * ! 외부에서 웰로 앱으로 돌아오는 용도로만 사용
   */
  ANDROID_APP_PREFIX = 'intent://action?web_link=',
}

export const ANDROID_DEEPLINK =
  '#Intent;scheme=welfarehello;package=com.wello.welfarehello;end';

export enum MESSAGE_KEYWORD {
  CHECK_KAKAO_INSTALLED = 'check-kakao-installed',
  CHECK_PERMISSION = 'check-permission',
  LOCATION = 'location',
  NOTIFICATION = 'notification',
  TEST_NOTIFICATION = 'test-notification',
  CAMERA = 'camera',
  IMAGE = 'image-picker',
  DOCUMENT = 'document-picker',
  LOGIN = 'login',
  VERSION = 'version',

  // 디바이스 광고 식별자 (AAID/IDFA) 요청 — 앱이 취득해 반환 (앱 키워드와 동기)
  GET_ADVERTISING_ID = 'get-advertising-id',

  SHARE_CONTENT = 'share-content',

  SHARE_LIST = 'share-list',

  EXIT_APP = 'exit-app',

  RESTART_APP = 'restart-app',

  // OS 설정 화면 열기 — 알림 권한 영구 거부 시 유일한 복구 경로 (앱 키워드와 동기)
  SYSTEM_SETTINGS = 'system-settings',

  HAPTIC = 'trigger-impact',

  STATUS_COLOR = 'change-status-color',

  // AdPopcorn 리워드
  OPEN_ADPOPCORN_BRIDGE = 'open-adpopcorn-bridge',
  OPEN_ADPOPCORN_OFFERWALL = 'open-adpopcorn-offerwall',
  OPEN_ADPOPCORN_CS = 'open-adpopcorn-cs',
  GET_ADPOPCORN_OFFERWALL_REWARD = 'get-adpopcorn-offerwall-reward',
  GET_ADPOPCORN_BRIDGE_REWARD = 'get-adpopcorn-bridge-reward',

  // AdPopcorn 이벤트 푸시 (앱 -> 웹)
  ADPOPCORN_SDK_EVENT = 'adpopcorn-sdk-event',

  // Pincrux 오퍼월 (usrkey를 open 시점에 전달)
  OPEN_PINCRUX_OFFERWALL = 'open-pincrux-offerwall',

  // Pincrux 이벤트 푸시 (앱 -> 웹): 오퍼월 닫고 복귀 시 잔액 갱신 힌트
  PINCRUX_REFRESH_BALANCE = 'pincrux-refresh-balance',

  // AdiSON 오퍼월 (uid를 open 시점에 전달)
  OPEN_ADISON_OFFERWALL = 'open-adison-offerwall',

  // AdPopcorn SSP (광고 미디에이션 - 전면영상/리워드영상)
  LOAD_ADPOPCORN_SSP_INTERSTITIAL_VIDEO = 'load-adpopcorn-ssp-interstitial-video',
  SHOW_ADPOPCORN_SSP_INTERSTITIAL_VIDEO = 'show-adpopcorn-ssp-interstitial-video',
  LOAD_ADPOPCORN_SSP_REWARD_VIDEO = 'load-adpopcorn-ssp-reward-video',
  SHOW_ADPOPCORN_SSP_REWARD_VIDEO = 'show-adpopcorn-ssp-reward-video',

  // AdPopcorn SSP 이벤트 푸시 (앱 -> 웹)
  ADPOPCORN_SSP_EVENT = 'adpopcorn-ssp-event',
}

export const ADPOPCORN_SDK_EVENT_NAME = {
  CLOSED_OFFER_WALL_PAGE: 'OnClosedOfferWallPage',
  COMPLETED_CAMPAIGN: 'OnCompletedCampaign',
  OFFERWALL_TOTAL_REWARD_INFO: 'OnOfferwallTotalRewardInfo',
  BRIDGE_TOTAL_REWARD_INFO: 'OnBridgeTotalRewardInfo',
} as const;

export type AdpopcornSdkEventName =
  (typeof ADPOPCORN_SDK_EVENT_NAME)[keyof typeof ADPOPCORN_SDK_EVENT_NAME];

// AdPopcorn SSP 모듈 구분 (앱->웹 ADPOPCORN_SSP_EVENT의 data.module 값, 앱 constants와 동기)
export const ADPOPCORN_SSP_MODULE = {
  INTERSTITIAL_VIDEO: 'interstitialVideo',
  REWARD_VIDEO: 'rewardVideo',
} as const;

export type AdpopcornSspModule =
  (typeof ADPOPCORN_SSP_MODULE)[keyof typeof ADPOPCORN_SSP_MODULE];

// ADPOPCORN_SSP_EVENT의 data 페이로드(전면영상/리워드영상): { module, event, payload }
export interface AdpopcornSspEventData {
  module: AdpopcornSspModule;
  event: string;
  payload?: unknown;
}

type JsonPrimitive = string | number | boolean | null;
type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };
type MessageParams = Record<string, JsonValue>;

export interface MessageRequest<TParams extends MessageParams = MessageParams> {
  id: string;
  type: MESSAGE_KEYWORD;
  params?: TParams;
}

export interface MessageResponse<TData = unknown> extends MessageRequest {
  id: string;
  type: MESSAGE_KEYWORD;
  isSuccess: boolean;
  message: string;
  data?: TData;
}

export type AppEventKeyword =
  | MESSAGE_KEYWORD.ADPOPCORN_SDK_EVENT
  | MESSAGE_KEYWORD.PINCRUX_REFRESH_BALANCE
  | MESSAGE_KEYWORD.ADPOPCORN_SSP_EVENT;

export interface MessageEvent<TData = unknown> {
  type: AppEventKeyword;
  data: TData;
}

export type AppBridgeMessage<TData = unknown> =
  MessageResponse<TData> | MessageEvent<TData>;
